<script setup>
import { inject } from 'vue'

const eventBus = inject('eventBus');
import { useDialogPluginComponent } from 'quasar'

defineEmits([...useDialogPluginComponent.emits])

const { dialogRef, onDialogHide, onDialogOK, onDialogCancel } = useDialogPluginComponent()
</script>

<template>
    <q-dialog ref="dialogRef" @hide="onDialogHide" style="max-height: 50vh" class="scroll">
        <q-card class="q-dialog-plugin">
            <q-card-section class="row items-center q-pb-none">
                <span class="text-h5 inline-block">PRIVACY POLICY</span>
                <q-space/>
                <q-btn v-close-popup dense flat icon="fa-solid fa-xmark" round/>
            </q-card-section>
            <q-separator/>
            <q-card-section class="scroll" style="max-height: 70vh;">
                <p class="text-weight-light text-italic">Last Updated: 15/7/24</p>
                <ol>
                    <li>
                        <span class="text-h7 text-weight-bold">INTRODUCTION</span>
                        <p>Memo Link ("we," "us," or "our") is committed to protecting the privacy of our users ("you"
                            or "your"). This Privacy Policy explains how we collect, use, disclose, and safeguard your
                            personal information when you use our website https://memo.link (the "Website") and our
                            services (collectively, the "Services").</p>
                        <p>Please read this Privacy Policy carefully. By accessing or using our Services, you
                            acknowledge that you have read, understood, and agree to be bound by this Privacy Policy. If
                            you do not agree with our policies and practices, please do not use our Services.</p>
                    </li>
                    <li>
                        <span class="text-h7 text-weight-bold">INFORMATION WE COLLECT</span>
                        <p>We collect several types of information from and about users of our Services, including:</p>
                        <ol>
                            <li>Personal Data: Information that identifies, relates to, describes, is reasonably capable
                                of being associated with, or could reasonably be linked, directly or indirectly, with a
                                particular consumer or household ("Personal Data"). This may include:
                                <ol class="alphabetical">
                                    <li>Contact information (e.g., email address)</li>
                                    <li>Usage Data (as defined below)</li>
                                    <li>Analytics data (e.g., number of users, session statistics)</li>
                                </ol>
                            </li>
                            <li>Usage Data: Information about your interaction with our Services, including:
                                <ol class="alphabetical">
                                    <li>IP addresses</li>
                                    <li>Browser type and version</li>
                                    <li>Operating system</li>
                                    <li>Pages visited and time spent on those pages</li>
                                    <li>Unique device identifiers</li>
                                    <li>Other diagnostic data</li>
                                </ol>
                            </li>
                            <li>Cookies and Similar Technologies: We use cookies and similar tracking technologies to
                                collect and store certain information. For more details, please refer to our
                                <a href="#" @click.prevent="eventBus.emit('showCookiePolicy')">Cookie Policy.</a>
                            </li>
                        </ol>
                    </li>
                    <li>
                        <span class="text-h7 text-weight-bold">HOW WE COLLECT INFORMATION</span>
                        <p>We collect information through:</p>
                        <ol>
                            <li>Direct interactions: When you provide information to us by filling in forms on our
                                Website or by corresponding with us.
                            </li>
                            <li>Automated technologies: As you navigate through and interact with our Services, we may
                                automatically collect certain information using cookies, web beacons, and other
                                technologies.
                            </li>
                            <li>Third parties: We may receive information about you from third-party service providers,
                                such as analytics providers and advertising networks.
                            </li>
                        </ol>
                    </li>
                    <li>
                        <span class="text-h7 text-weight-bold">HOW WE USE YOUR INFORMATION</span>
                        <p>We use the information we collect for various purposes, including:</p>
                        <ol>
                            <li>Providing and maintaining our Services</li>
                            <li>Improving and personalizing our Services</li>
                            <li>Analyzing usage patterns and trends</li>
                            <li>Communicating with you about our Services</li>
                            <li>Enforcing our terms, conditions, and policies</li>
                            <li>Complying with legal obligations</li>
                        </ol>
                    </li>
                    <li>
                        <span class="text-h7 text-weight-bold">DISCLOSURE OF YOUR INFORMATION</span>
                        <p>We may disclose your Personal Data to:</p>
                        <ol>
                            <li>Service Providers: Third-party vendors who perform services on our behalf.</li>
                            <li>Affiliates: Our subsidiaries and affiliates.</li>
                            <li>Business Transfers: A buyer or other successor in the event of a merger, divestiture,
                                restructuring, reorganization, dissolution, or other sale or transfer of some or all of
                                our assets.
                            </li>
                            <li>Legal Requirements: To comply with any court order, law, or legal process, including to
                                respond to any government or regulatory request.
                            </li>
                        </ol>
                    </li>
                    <li>
                        <span class="text-h7 text-weight-bold">DATA RETENTION</span>
                        <p>We will retain your Personal Data only for as long as necessary to fulfill the purposes for
                            which it was collected, including for the purposes of satisfying any legal, accounting, or
                            reporting requirements.</p>
                    </li>
                    <li>
                        <span class="text-h7 text-weight-bold">DATA SECURITY</span>
                        <p>We have implemented appropriate technical and organizational measures to secure your Personal
                            Data from accidental loss and unauthorized access, use, alteration, and disclosure. However,
                            no method of transmission over the Internet or method of electronic storage is 100%
                            secure.</p>
                    </li>
                    <li>
                        <span class="text-h7 text-weight-bold">YOUR RIGHTS AND CHOICES</span>
                        <p>Depending on your jurisdiction, you may have certain rights regarding your Personal Data,
                            including:</p>
                        <ol>
                            <li>Access: The right to request access to your Personal Data.</li>
                            <li>Correction: The right to request that we correct incomplete or inaccurate Personal
                                Data.
                            </li>
                            <li>Erasure: The right to request that we delete your Personal Data.</li>
                            <li>Restriction: The right to request that we restrict the processing of your Personal
                                Data.
                            </li>
                            <li>Data Portability: The right to receive a copy of your Personal Data in a structured,
                                machine-readable format.
                            </li>
                            <li>Objection: The right to object to our processing of your Personal Data.
                                <p>To exercise any of these rights, please contact us using the information provided in
                                    Section 12.</p>
                            </li>
                        </ol>
                    </li>
                    <li>
                        <span class="text-h7 text-weight-bold">INTERNATIONAL DATA TRANSFERS</span>
                        <p>Your Personal Data may be transferred to and processed in countries other than your country
                            of residence. These countries may have data protection laws that are different from the laws
                            of your country.</p>
                        <p>We have taken appropriate safeguards to ensure that your Personal Data remains protected in
                            accordance with this Privacy Policy when transferred internationally.</p>
                    </li>
                    <li>
                        <span class="text-h7 text-weight-bold">CHILDREN'S PRIVACY</span>
                        <p>Our Services are not intended for children under the age of 13. We do not knowingly collect
                            Personal Data from children under 13. If you are a parent or guardian and believe that your
                            child has provided us with Personal Data, please contact us.</p>
                    </li>
                    <li>
                        <span class="text-h7 text-weight-bold">CHANGES TO OUR PRIVACY POLICY</span>
                        <p>We may update this Privacy Policy from time to time. We will notify you of any changes by
                            posting the new Privacy Policy on this page and updating the "Last Updated" date.</p>
                    </li>
                    <li>
                        <span class="text-h7 text-weight-bold">CONTACT INFORMATION</span>
                        <p>If you have any questions about this Privacy Policy or our privacy practices, please contact
                            us at:</p>
                        <p>
                            <span class="text-italic text-weight-bold">Memo Link</span><br>
                            <span class="text-italic text-weight-bold">Email: legal@memo.link</span>
                        </p>
                    </li>
                    <li>
                        <span class="text-h7 text-weight-bold">ADDITIONAL INFORMATION FOR EU RESIDENTS</span>
                        <p>If you are a resident of the European Union, you may have additional rights under the General
                            Data Protection Regulation (GDPR). For more information about these rights and how to
                            exercise them, please contact us using the information provided in Section 12.</p>
                    </li>
                    <li>
                        <span class="text-h7 text-weight-bold">CALIFORNIA PRIVACY RIGHTS</span>
                        <p>If you are a California resident, you may have additional rights under the California
                            Consumer Privacy Act (CCPA). For more information about these rights and how to exercise
                            them, please contact us using the information provided in Section 12.</p>
                    </li>
                    <li>
                        <span class="text-h7 text-weight-bold">GOVERNING LAW</span>
                        <p>This Privacy Policy shall be governed by and construed in accordance with the laws of Rome,
                            Italy, without regard to its conflict of law provisions.</p>
                    </li>
                    <li>
                        <span class="text-h7 text-weight-bold">SEVERABILITY</span>
                        <p>If any provision of this Privacy Policy is held to be unenforceable or invalid, such
                            provision will be changed and interpreted to accomplish the objectives of such provision to
                            the greatest extent possible under applicable law, and the remaining provisions will
                            continue in full force and effect.</p>
                        <p>By using our Services, you acknowledge that you have read and understood this Privacy Policy
                            and agree to its terms.</p>
                        <p>This revised privacy policy provides a more comprehensive and professionally structured
                            document. It covers key areas such as data collection, use, disclosure, retention, security,
                            and user rights. It also includes sections on international data transfers, children's
                            privacy, and specific information for EU and California residents. As always, it's advisable
                            to have this document reviewed by a legal professional to ensure it fully complies with all
                            applicable laws and regulations in your jurisdiction.</p>
                    </li>
                </ol>
            </q-card-section>
        </q-card>
    </q-dialog>
</template>

<style scoped>
ol {
    counter-reset: item;
    padding-left: 10px;
}

ol > li {
    display: block;
    margin-top: 5px;
    margin-bottom: 5px;
}

ol > li:before,
ol > li > ol > li:before {
    content: counters(item, ".") ". ";
    counter-increment: item;
    font-weight: bold;
}

ol > li > ol.alphabetical{
    counter-reset: list;
}

ol > li > ol.alphabetical > li:before {
    content: counter(list, lower-alpha) ") ";
    counter-increment: list;
}
.q-card.q-dialog-plugin {
    width: 800px;
    max-width: 80vw;
}

@media screen and (max-width: 450px) {
    .q-card.q-dialog-plugin {
        width: 400px;
        max-width: 100vw;
    }
}
</style>

<style>
@media screen and (max-width: 450px) {
    .q-dialog__inner.q-dialog__inner--minimized {
        padding: 24px 15px;
    }
}
</style>
